/* eslint-disable max-len */
import { ImportedReserveInstruction, ReserveImportError } from 'models'
import { capitalizeFirstLetter } from 'utils/helpers'
import { normaliseImmat } from 'utils/helpers/format'
import useGenerateColumns from './useGenerateColumns'

const useCheckHeaders = () => {
  const columns = useGenerateColumns()

  const customImmat = columns.find((col) => col.id === 'licensePlate')?.header.toLowerCase()
  const customReserve = columns.find((col) => col.id === 'reserveHTHF')?.header.toLowerCase()

  const allowedImmatTerms = ['immatriculation', 'immat', 'immat.']
  const allowedReserveTerms = ['réserve', 'reserve', 'reserven', 'reservehthf', 'reserveTTCHF']
  const allowedTerms = {
    immatriculation:
      customImmat !== undefined && !allowedImmatTerms.includes(customImmat)
        ? [...allowedImmatTerms, customImmat]
        : allowedImmatTerms,

    reserve:
      customReserve !== undefined && !allowedReserveTerms.includes(customReserve)
        ? [...allowedReserveTerms, customReserve]
        : allowedReserveTerms,
  }

  const standardizeHeadersName = (headers: Array<string>) => {
    const standardizedHeaders = headers.map((header) => {
      const immatIndex = allowedTerms.immatriculation.indexOf(header)
      if (immatIndex !== -1) {
        return 'importImmat'
      }

      const reserveIndex = allowedTerms.reserve.indexOf(header)
      if (reserveIndex !== -1) {
        return 'importReserve'
      }
      return header
    })

    return standardizedHeaders
  }

  const standardizeParsedCSV = (parsedCSV: string[][]) => {
    const csvHeaders = parsedCSV[0]
    const standardizedHeaders = standardizeHeadersName(csvHeaders)

    // Turn array of string into array of objects with only 'immat' and 'reserve' as keys
    const immatIndex = standardizedHeaders.indexOf('importImmat')
    const reserveIndex = standardizedHeaders.indexOf('importReserve')

    /// Chop off array's header line
    const parsedCSVWithoutHeader = parsedCSV.filter((_line, index) => index > 0)

    const standardizedParsedCSV = parsedCSVWithoutHeader.map((line) => {
      const importReserveWithoutSpaces = line[reserveIndex].replace(/\s/g, '').replace(/,/g, '.')
      const isAnInteger = parseFloat(importReserveWithoutSpaces) % 1 === 0
      const instruction: ImportedReserveInstruction = {
        importImmat: normaliseImmat(line[immatIndex]),
        importReserve: isAnInteger ? parseInt(importReserveWithoutSpaces, 10) : NaN,
      }
      return instruction
    })

    return standardizedParsedCSV
  }

  const countOccurrences = (arrayToCheck: string[], referenceArray: string[]) => {
    let count = 0
    arrayToCheck.map((str) => {
      if (referenceArray.includes(str)) {
        count += 1
      }
      return null
    })

    return count
  }

  const createHeaderErrorMessage = (
    allowedHeaderTerms: Array<string>,
    headerOccurrences: number,
  ): string => {
    if (headerOccurrences === 1) return ''

    const headerName = capitalizeFirstLetter(allowedHeaderTerms[0])
    const headerAuthorizedNames = allowedHeaderTerms.join(', ')

    const containsMessagePart =
      headerOccurrences === 0 ? 'ne contient aucune colonne' : 'contient plusieurs colonnes'

    const errorMessage = `Le fichier ${containsMessagePart} "${headerName}" (Noms de colonnes autorisés: ${headerAuthorizedNames})`

    return errorMessage
  }

  const handleHeaderNameErrors = (
    csvHeaders: Array<string>,
    allowedHeaderTerms: Array<string>,
  ): ReserveImportError => {
    const headerOccurrences = countOccurrences(csvHeaders, allowedHeaderTerms)

    return {
      errorMessage: createHeaderErrorMessage(allowedHeaderTerms, headerOccurrences),
      hasError: headerOccurrences !== 1,
    }
  }

  // Check for errors in header
  const checkHeadersForImmat = (csvHeaders: Array<string>): ReserveImportError => {
    return handleHeaderNameErrors(csvHeaders, allowedTerms.immatriculation)
  }

  const checkHeadersForReserve = (csvHeaders: Array<string>): ReserveImportError => {
    return handleHeaderNameErrors(csvHeaders, allowedTerms.reserve)
  }

  return {
    allowedTerms,
    countOccurrences,
    createHeaderErrorMessage,
    handleHeaderNameErrors,
    checkHeadersForImmat,
    checkHeadersForReserve,
    standardizeParsedCSV,
  }
}

export default useCheckHeaders
